import { useEffect, useRef } from 'react';

/**
 * Hook to trap focus within a container element.
 * @param ref - The ref of the container to trap focus in.
 * @param isActive - Whether the trap is active.
 * @param onClose - Optional callback to run when Escape is pressed.
 */
export const useFocusTrap = (
  ref: React.RefObject<HTMLElement>,
  isActive: boolean,
  onClose?: () => void
) => {
  const previousFocus = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (isActive && ref.current) {
      previousFocus.current = document.activeElement as HTMLElement;

      const focusableElements = ref.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length > 0) {
        (focusableElements[0] as HTMLElement).focus();
      }

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && onClose) {
          onClose();
          return;
        }

        if (e.key === 'Tab') {
          const focusable = ref.current?.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );
          if (!focusable || focusable.length === 0) return;

          const first = focusable[0] as HTMLElement;
          const last = focusable[focusable.length - 1] as HTMLElement;

          if (e.shiftKey) {
            if (document.activeElement === first) {
              e.preventDefault();
              last.focus();
            }
          } else {
            if (document.activeElement === last) {
              e.preventDefault();
              first.focus();
            }
          }
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        if (previousFocus.current) {
          previousFocus.current.focus();
        }
      };
    }
  }, [isActive, onClose, ref]);
};
